import {
  ChangeEvent,
  FC,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState
} from "react"
import { cn } from "../../utils"
import BBSwitch from "../form/Switch"

export const CONFIG_ADMIN_INPUT_CLASS =
  "w-full max-w-xl px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"

export const CONFIG_ADMIN_INPUT_FULL_CLASS =
  "w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"

function ConfigAdminFieldKeySuffix({ fieldKey }: { fieldKey: string }) {
  return (
    <span className="text-xs font-normal text-gray-400">
      {" · "}
      <span className="font-mono">{fieldKey}</span>
    </span>
  )
}

interface ConfigAdminFieldLabelHintProps {
  label: string
  hint?: string
  htmlFor?: string
  /** Config / API key shown after the label (smaller, gray). */
  fieldKey?: string
}

export const ConfigAdminFieldLabelHint: FC<ConfigAdminFieldLabelHintProps> = ({
  label,
  hint,
  htmlFor,
  fieldKey
}) => (
  <>
    <label htmlFor={htmlFor} className="mb-0.5 block">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      {fieldKey ? <ConfigAdminFieldKeySuffix fieldKey={fieldKey} /> : null}
    </label>
    {hint ? <p className="text-xs text-gray-400 mb-1.5">{hint}</p> : null}
  </>
)

interface ConfigAdminPanelStackedFieldProps {
  label: string
  hint?: string
  htmlFor?: string
  fieldKey?: string
  dividerBottom?: boolean
  children: ReactNode
}

export const ConfigAdminPanelStackedField: FC<
  ConfigAdminPanelStackedFieldProps
> = ({ label, hint, htmlFor, fieldKey, dividerBottom = true, children }) => (
  <div className={cn("px-4 py-3", dividerBottom && "border-b border-gray-100")}>
    <ConfigAdminFieldLabelHint
      label={label}
      hint={hint}
      htmlFor={htmlFor}
      fieldKey={fieldKey}
    />
    {children}
  </div>
)

interface ConfigAdminNumberInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "type"
  > {
  value: number | null | undefined
  onChange: (value: number | null) => void
  /** When true, empty blur → null. Default false → blur commits fallback. */
  nullable?: boolean
  /** Value committed on blur when field is empty. Default 0. Ignored when nullable=true. */
  fallback?: number
  /** "int" | "float". Default "float". */
  parse?: "int" | "float"
  /** Optional clamp applied after parsing. */
  clamp?: (v: number) => number
}

export const ConfigAdminNumberInput: FC<ConfigAdminNumberInputProps> = ({
  value,
  onChange,
  nullable = false,
  fallback = 0,
  parse: parseMode = "float",
  clamp,
  className = CONFIG_ADMIN_INPUT_CLASS,
  ...rest
}) => {
  const doParse = (s: string) =>
    parseMode === "int" ? parseInt(s, 10) : parseFloat(s)

  const [display, setDisplay] = useState<string>(
    value == null ? "" : String(value)
  )
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) {
      setDisplay(value == null ? "" : String(value))
    }
  }, [value])

  const commit = (raw: string) => {
    const parsed = doParse(raw)
    if (isNaN(parsed)) {
      if (nullable) {
        onChange(null)
        setDisplay("")
      } else {
        onChange(fallback)
        setDisplay(String(fallback))
      }
      return
    }
    const final = clamp ? clamp(parsed) : parsed
    onChange(final)
    setDisplay(String(final))
  }

  return (
    <input
      type="number"
      value={display}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value
        setDisplay(raw)
        const parsed = doParse(raw)
        if (!isNaN(parsed)) {
          onChange(clamp ? clamp(parsed) : parsed)
        }
      }}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        commit(display)
      }}
      className={className}
      {...rest}
    />
  )
}

interface ConfigAdminToggleRowProps {
  label: string
  /** Config key shown after the label (smaller, gray). Omit when label is already the raw key. */
  fieldKey?: string
  description?: string
  enabled: boolean
  onChange: (val: boolean) => void
  onDelete?: () => void
  last?: boolean
}

export const ConfigAdminToggleRow: FC<ConfigAdminToggleRowProps> = ({
  label,
  fieldKey,
  description,
  enabled,
  onChange,
  onDelete,
  last
}) => (
  <div
    className={cn(
      "flex items-center justify-between px-4 py-3 transition-colors hover:bg-gray-50",
      !last && "border-b border-gray-100"
    )}
  >
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-700">
        <span>{label}</span>
        {fieldKey ? <ConfigAdminFieldKeySuffix fieldKey={fieldKey} /> : null}
      </p>
      {description ? (
        <p className="text-xs text-gray-400">{description}</p>
      ) : null}
    </div>
    <div className="ml-4 flex shrink-0 items-center gap-3">
      <BBSwitch enabled={enabled} setEnabled={onChange} />
      {onDelete ? (
        <button
          type="button"
          onClick={onDelete}
          className="text-lg leading-none text-gray-300 transition-colors hover:text-red-400"
          aria-label={`Remove ${label}`}
        >
          ×
        </button>
      ) : null}
    </div>
  </div>
)
